// In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Network, Search, Cpu, Shield, Database, LayoutGrid, Clock, AlertTriangle, Play, Sparkles, Check, DollarSign, ArrowRight,
  ListTodo, CheckSquare, Square, Zap, Lock
} from 'lucide-react';
import { NexoraXD3Visualizer } from './NexoraXD3Visualizer';

interface BlockItem {
  id: number;
  name: string;
  category: string;
  subNodesCount: number;
  description: string;
  depositAmount: number;
}

export const NexoraXMasterEngine: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'atlas' | 'd3-map'>('atlas');
  const [search, setSearch] = useState('');
  const [activeBlock, setActiveBlock] = useState<BlockItem | null>(null);
  const [isLocked, setIsLocked] = useState<boolean>(false);
  const [trialTimeLeft, setTrialTimeLeft] = useState<number>(60);
  const [simulationStatus, setSimulationStatus] = useState<'idle' | 'running' | 'complete'>('idle');
  const [percentage, setPercentage] = useState<number>(0);

  // Multi-Select States
  const [isMultiSelectMode, setIsMultiSelectMode] = useState<boolean>(false);
  const [selectedBlockIds, setSelectedBlockIds] = useState<number[]>([]);
  const [bulkStatus, setBulkStatus] = useState<'idle' | 'running_deploy' | 'running_lockdown' | 'complete_deploy' | 'complete_lockdown'>('idle');
  const [bulkProgress, setBulkProgress] = useState<number>(0);

  // 60S Temporal Session countdown
  React.useEffect(() => {
    if (trialTimeLeft > 0 && !isLocked) {
      const timer = setTimeout(() => setTrialTimeLeft(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (trialTimeLeft === 0) {
      setIsLocked(true);
    }
  }, [trialTimeLeft, isLocked]);

  const handleResetLock = () => {
    setTrialTimeLeft(60);
    setIsLocked(false);
    setSimulationStatus('idle');
    setPercentage(0);
    setBulkStatus('idle');
    setBulkProgress(0);
  };

  // Generate 100 blocks dynamically representing different structural branches
  const masterMatrix = useMemo<BlockItem[]>(() => {
    const categories = ['Defense Grid', 'Fiscal Ledger', 'Automation Swarm', 'Workspace Enclave', 'Hardware Node', 'Transit Carrier'];
    return Array.from({ length: 100 }, (_, i) => {
      const blockId = i + 1;
      let category = categories[i % categories.length];
      let name = `Enterprise Block ${blockId}: `;
      let description = '';
      let depositAmount = 500;

      if (blockId <= 15) {
        category = 'Defense Grid';
        name += `Quantum Sentry ${blockId}`;
        description = 'Symmetric perimeter tracking shielding live container deployments and mitigating unauthorized host shifts.';
        depositAmount = 750;
      } else if (blockId <= 40) {
        category = 'Fiscal Ledger';
        name += `Chronos Node ${blockId}`;
        description = 'Forensic ledger synchronization pipeline evaluating global multi-stage tax protocols and Secure Payment Provider routing.';
        depositAmount = 1500;
      } else if (blockId <= 65) {
        category = 'Automation Swarm';
        name += `Cognitive Agent ${blockId}`;
        description = 'Self-healing worker micro-networks driving conversational automation, recursive feedback, and smart contracts.';
        depositAmount = 1000;
      } else if (blockId <= 80) {
        category = 'Workspace Enclave';
        name += `NexoraX Resource Library Proxy ${blockId}`;
        description = 'Isolated file system mirroring engine backing up active directory states to verified remote cloud locations.';
        depositAmount = 2500;
      } else {
        category = 'Hardware Node';
        name += `Cryo-Chamber Sensor ${blockId}`;
        description = 'Environmental telemetry monitor optimizing geothermal server core metrics with 0.8s system dispatch goals.';
        depositAmount = 5000;
      }

      return {
        id: blockId,
        name,
        category,
        subNodesCount: 100, // Fixed 100 Sub-Nodes density as specified
        description,
        depositAmount
      };
    });
  }, []);

  // Filter 10,000 subnodes conceptually based on search query matching block parameters
  const filteredBlocks = useMemo(() => {
    if (!search) return masterMatrix;
    const query = search.toLowerCase();
    return masterMatrix.filter(b => 
      b.name.toLowerCase().includes(query) || 
      b.category.toLowerCase().includes(query) ||
      b.description.toLowerCase().includes(query) ||
      `block ${b.id}`.includes(query)
    );
  }, [search, masterMatrix]);

  const runSimulation = () => {
    if (isLocked || !activeBlock) return;
    setSimulationStatus('running');
    setPercentage(0);

    const interval = setInterval(() => {
      setPercentage(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setSimulationStatus('complete');
          return 100;
        }
        return prev + 10;
      });
    }, 150);
  };

  // Block Click Operations
  const handleBlockSelection = (block: BlockItem) => {
    if (isLocked) return;
    
    if (isMultiSelectMode) {
      setSelectedBlockIds(prev => 
        prev.includes(block.id) 
          ? prev.filter(id => id !== block.id) 
          : [...prev, block.id]
      );
    } else {
      setActiveBlock(block);
      setSelectedBlockIds([block.id]);
      setSimulationStatus('idle');
      setPercentage(0);
    }
  };

  const handleSelectAllFiltered = () => {
    if (isLocked) return;
    setSelectedBlockIds(filteredBlocks.map(b => b.id));
  };

  const handleClearAllSelected = () => {
    setSelectedBlockIds([]);
  };

  const triggerBulkDeploy = () => {
    if (selectedBlockIds.length === 0 || isLocked) return;
    setBulkStatus('running_deploy');
    setBulkProgress(0);

    const interval = setInterval(() => {
      setBulkProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setBulkStatus('complete_deploy');
          return 100;
        }
        return prev + 10;
      });
    }, 120);
  };

  const triggerBulkLockdown = () => {
    if (selectedBlockIds.length === 0 || isLocked) return;
    setBulkStatus('running_lockdown');
    setBulkProgress(0);

    const interval = setInterval(() => {
      setBulkProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setBulkStatus('complete_lockdown');
          return 100;
        }
        return prev + 10;
      });
    }, 120);
  };

  // Single Block Financial splits
  const depositVal = activeBlock?.depositAmount || 1000;
  const levyVal = depositVal * 0.01;
  const splitVal = levyVal / 3;
  const afterLevyVal = depositVal - levyVal;
  const gavelFeeVal = afterLevyVal * 0.20;
  const netSettleVal = afterLevyVal - gavelFeeVal;

  // Cumulative selected financial splits
  const bulkTotalDeposit = useMemo(() => {
    return masterMatrix
      .filter(b => selectedBlockIds.includes(b.id))
      .reduce((sum, b) => sum + b.depositAmount, 0);
  }, [selectedBlockIds, masterMatrix]);

  const bulkLevyVal = bulkTotalDeposit * 0.01;
  const bulkSplitVal = bulkLevyVal / 3;
  const bulkAfterLevyVal = bulkTotalDeposit - bulkLevyVal;
  const bulkGavelFeeVal = bulkAfterLevyVal * 0.20;
  const bulkNetSettleVal = bulkAfterLevyVal - bulkGavelFeeVal;

  return (
    <div id="NexoraX-master-engine-root" className="w-full max-w-7xl mx-auto p-4 md:p-8 space-y-6 bg-black text-slate-100 rounded-3xl border border-amber-500/30 shadow-[0_0_50px_rgba(217,119,6,0.06)] relative overflow-hidden">
      
      {/* Background visual neon grid lines */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#0c0f1d_1px,transparent_1px),linear-gradient(to_bottom,#0c0f1d_1px,transparent_1px)] bg-[size:3rem_3rem] opacity-30 pointer-events-none" />

      {/* Ticker / Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-neutral-900 pb-6 relative z-10">
        <div>
          <div className="flex items-center gap-2 text-amber-500 font-mono text-xs font-bold uppercase tracking-widest">
            <LayoutGrid className="w-4 h-4 text-amber-500 animate-pulse" />
            <span>Grand Unified Node Network</span>
          </div>
          <h2 className="text-xl md:text-2xl font-sans font-black text-white uppercase tracking-wider mt-1.5">
            NexoraX Unified Empire Matrix <span className="text-amber-500 font-mono">(10,000-Node Density)</span>
          </h2>
          <p className="text-[10px] text-slate-500 font-mono tracking-wider mt-1 uppercase">
            NexoraX Ledger Sync: Director Lead Director // 100 Blocks × 100 Sub-Nodes Integration
          </p>
        </div>

        {/* Temporal Lock Status Widget */}
        <div className="flex items-center gap-3 shrink-0 col-span-1">
          {isLocked && (
            <button 
              onClick={handleResetLock}
              className="px-2.5 py-1.5 border border-amber-500/30 bg-amber-950/20 hover:bg-amber-900/40 text-amber-400 font-mono text-[9px] uppercase font-bold tracking-wider rounded-lg transition-all"
            >
              Unlock Demo
            </button>
          )}
          <div className={`px-4 py-2 border rounded-xl font-mono text-xs font-bold tracking-widest transition-all duration-300 flex items-center gap-2 ${
            isLocked 
              ? 'border-red-600 bg-red-950/40 text-red-400 animate-pulse' 
              : 'border-amber-500 bg-amber-950/40 text-amber-500'
          }`}>
            <Clock className={`w-3.5 h-3.5 ${!isLocked ? 'animate-spin' : ''}`} />
            <span>
              {isLocked 
                ? "EXPIRE: TEMPORAL PROTECTION ENGAGED" 
                : `SESSION TTL: 00:${trialTimeLeft < 10 ? '0' + trialTimeLeft : trialTimeLeft}`}
            </span>
          </div>
        </div>
      </div>

      {/* Dynamic Navigation Sub-Tabs */}
      <div className="flex border-b border-neutral-900 pb-px relative z-10 gap-6">
        <button
          onClick={() => setActiveSection('atlas')}
          className={`pb-3 text-xs font-mono font-bold tracking-widest uppercase transition-all relative ${
            activeSection === 'atlas' 
              ? 'text-amber-500 border-b-2 border-amber-500' 
              : 'text-slate-500 hover:text-slate-200'
          }`}
        >
          Node Matrix Grid Atlas
        </button>
        <button
          onClick={() => setActiveSection('d3-map')}
          className={`pb-3 text-xs font-mono font-bold tracking-widest uppercase transition-all relative flex items-center gap-2 ${
            activeSection === 'd3-map' 
              ? 'text-amber-500 border-b-2 border-amber-500' 
              : 'text-slate-500 hover:text-slate-200'
          }`}
        >
          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
          D3 Visual Hierarchy (Nodes 91-100)
        </button>
      </div>

      {activeSection === 'atlas' ? (
        /* Grid of details */
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 relative z-10">
        {/* Left Span 8: 100-Block Atlas, Multi-Select Mechanics & Search */}
        <div className="lg:col-span-8 flex flex-col space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input 
                type="text" 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search 10,000+ operational nodes (e.g., 'Sentry', 'Chronos', 'Cryo')..."
                className="w-full bg-[#050508] border border-neutral-800 text-slate-200 placeholder-slate-600 pl-11 pr-4 py-3 rounded-xl text-xs font-mono tracking-wide focus:border-amber-500/50 focus:outline-none transition-all"
              />
              {search && (
                <button 
                  onClick={() => setSearch('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[9px] font-mono text-slate-500 hover:text-white uppercase"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Toggle Multi-Select Mode Control */}
            <div className="flex gap-2.5">
              <button
                type="button"
                onClick={() => {
                  if (selectedBlockIds.length === masterMatrix.length) {
                    setSelectedBlockIds([]);
                  } else {
                    setSelectedBlockIds(masterMatrix.map(b => b.id));
                    setIsMultiSelectMode(true);
                  }
                }}
                className={`px-4 py-3 rounded-xl border text-[10px] font-mono uppercase tracking-wider font-bold transition-all flex items-center gap-2 justify-center bg-[#050508] ${
                  selectedBlockIds.length === masterMatrix.length
                    ? 'border-amber-500 text-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.15)] bg-amber-950/25'
                    : 'border-neutral-800 hover:border-neutral-700 text-slate-400 hover:text-white'
                }`}
              >
                <ListTodo className="w-3.5 h-3.5 text-amber-500" />
                <span>{selectedBlockIds.length === masterMatrix.length ? "Clear All 100" : "Select All 100"}</span>
              </button>

              <button
                onClick={() => {
                  setIsMultiSelectMode(!isMultiSelectMode);
                  if (!isMultiSelectMode) {
                    // Pre-fill selection with active block if selected
                    setSelectedBlockIds(activeBlock ? [activeBlock.id] : []);
                  }
                }}
                className={`px-4 py-3 rounded-xl border text-[10px] font-mono uppercase tracking-wider font-bold transition-all flex items-center gap-2 justify-center ${
                  isMultiSelectMode
                    ? 'border-amber-500 bg-amber-950/30 text-white shadow-[0_0_15px_rgba(245,158,11,0.15)]'
                    : 'border-neutral-800 hover:border-neutral-700 text-slate-400 hover:text-white'
                }`}
              >
                <CheckSquare className="w-3.5 h-3.5 text-amber-500" />
                <span>{isMultiSelectMode ? "Exit Multi" : "Multi-Select"}</span>
              </button>
            </div>
          </div>

          {/* Conditional Multi-Select Fast-Action Bar */}
          <AnimatePresence>
            {isMultiSelectMode && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="p-3 bg-neutral-950/90 border border-amber-500/20 rounded-xl flex flex-wrap gap-2 items-center justify-between"
              >
                <div className="flex items-center gap-2 text-xs font-mono text-zinc-400">
                  <span className="font-extrabold text-amber-500">
                    {selectedBlockIds.length} of {filteredBlocks.length}
                  </span>
                  <span>Blocks Assigned</span>
                  <span className="text-zinc-600">({selectedBlockIds.length * 100} virtual sub-nodes)</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSelectAllFiltered}
                    className="px-2.5 py-1.5 border border-zinc-800 hover:border-zinc-700 bg-zinc-900/40 text-zinc-300 font-mono text-[9px] uppercase font-bold tracking-wider rounded-lg transition-all"
                  >
                    Select All Visible
                  </button>
                  <button
                    onClick={handleClearAllSelected}
                    className="px-2.5 py-1.5 border border-zinc-850 hover:border-zinc-800 bg-black text-slate-500 hover:text-white font-mono text-[9px] uppercase font-bold tracking-wider rounded-lg transition-all"
                  >
                    Clear Selections
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex justify-between items-center px-1">
            <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest">
              Grid Blocks Atlas ({filteredBlocks.length} of 100)
            </span>
            <span className="text-[9px] font-mono text-[#f59e0b] tracking-wider uppercase">
              100 Sub-Nodes Per Block Fully Virtualized
            </span>
          </div>

          {/* Container Scroll Atlas */}
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-[420px] overflow-y-auto pr-1 customize-scrollbar">
            {filteredBlocks.map((block) => {
              const isSelected = isMultiSelectMode 
                ? selectedBlockIds.includes(block.id)
                : activeBlock?.id === block.id;
              
              return (
                <button
                  key={block.id}
                  disabled={isLocked}
                  onClick={() => handleBlockSelection(block)}
                  className={`p-3 text-left border rounded-xl font-mono transition-all duration-200 relative group ${
                    isLocked 
                      ? 'border-slate-900 bg-slate-950/10 text-slate-700 cursor-not-allowed'
                      : isSelected 
                        ? 'border-amber-500 bg-amber-950/30 text-white shadow-[0_0_15px_rgba(245,158,11,0.1)]' 
                        : 'border-neutral-900 hover:border-neutral-800 bg-neutral-950/40 text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <div className="flex justify-between items-center text-[9px] font-bold text-amber-500/70">
                    <span className="flex items-center gap-1.5">
                      {isMultiSelectMode ? (
                        isSelected ? (
                          <CheckSquare className="w-2.5 h-2.5 text-amber-500" />
                        ) : (
                          <Square className="w-2.5 h-2.5 text-zinc-700 group-hover:text-zinc-500" />
                        )
                      ) : null}
                      <span>BLOCK {block.id < 10 ? '0' + block.id : block.id}</span>
                    </span>
                    <span className="text-[8px] bg-slate-900 px-1 py-0.5 rounded text-slate-500 border border-slate-950">100N</span>
                  </div>
                  <div className="text-[10px] font-bold text-slate-300 mt-2 truncate max-w-full">
                    {block.name.split(':')[1]?.trim() || block.name}
                  </div>
                  <div className="text-[8px] text-slate-600 mt-1 truncate">
                    {block.category}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Span 4: Active Block Control Bench OR Multi-Select Deck */}
        <div className="lg:col-span-4 bg-gradient-to-br from-[#050508] to-black rounded-2xl border border-neutral-900 p-5 flex flex-col justify-between min-h-[440px] relative">
          
          {/* Locked Overlay block */}
          <AnimatePresence>
            {isLocked && (
              <motion.div 
                initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
                animate={{ opacity: 1, backdropFilter: 'blur(6px)' }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/95 flex flex-col justify-center items-center p-6 text-center z-40 rounded-2xl border border-red-500/20"
              >
                <div className="w-12 h-12 rounded-full border border-red-600/30 bg-red-950/20 flex justify-center items-center mb-4 text-red-500 animate-pulse">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <h4 className="text-xs font-mono font-black text-red-500 uppercase tracking-widest">
                  Temporal Freeze Triggered
                </h4>
                <p className="text-slate-500 font-mono text-[9px] max-w-xs mt-2.5 mb-5 uppercase tracking-wide leading-relaxed">
                  Unauthorized inactive session detected. Deploy a NexoraX matrix key to bypass temporal limits.
                </p>
                <button
                  onClick={handleResetLock}
                  className="w-full max-w-xs py-2 bg-amber-500 hover:bg-amber-400 text-black font-mono text-[10px] font-extrabold uppercase tracking-widest rounded-lg transition-all"
                >
                  Regenerate Handshake Block
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {isMultiSelectMode ? (
            /* Multi-Select Bulk Decisions Panel */
            <div className="space-y-5 flex-1 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="flex justify-between items-center text-[9px] font-mono text-slate-500 border-b border-neutral-900 pb-3 mb-1 uppercase tracking-wider font-bold">
                  <span>Bulk Decisions Decryptor</span>
                  <span className="text-[#f59e0b] font-black">MULTIPLE BLOCKS</span>
                </div>

                <div>
                  <span className="text-[8px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Selected Range Classification:</span>
                  <div className="text-xs font-bold text-[#f59e0b] uppercase font-mono tracking-wider">
                    {selectedBlockIds.length} ACTIVE CLUSTERS
                  </div>
                  <h3 className="text-sm font-black text-white uppercase tracking-wider font-sans mt-1">
                    Direct High-Density Swarm Routing
                  </h3>
                  <p className="text-slate-400 font-sans text-xs mt-2.5 leading-relaxed">
                    Executing collective deployment or perimeter lockdown protocols targeting {selectedBlockIds.length * 100} Virtual Nodes inside the Unified Matrix network.
                  </p>
                </div>

                {bulkStatus.startsWith('running_') && (
                  <div className="space-y-2 font-mono text-xs">
                    <div className="flex justify-between text-amber-500 font-extrabold uppercase">
                      <span>{bulkStatus === 'running_deploy' ? "BROADCAST DEPLOYING..." : "LOCKING Nodes RANGE..."}</span>
                      <span>{bulkProgress}%</span>
                    </div>
                    <div className="w-full bg-[#0a0a0f] h-1.5 rounded-full overflow-hidden border border-neutral-900">
                      <div className="bg-amber-500 h-full transition-all duration-100" style={{ width: `${bulkProgress}%` }} />
                    </div>
                  </div>
                )}

                {bulkStatus === 'complete_deploy' && (
                  <div className="p-3 border border-emerald-500/20 bg-emerald-950/10 rounded-xl flex items-center gap-2.5">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                    <span className="text-[9.5px] font-mono text-emerald-400 uppercase font-black tracking-wider leading-snug">
                      Bulk deploy successfully broadcast to {selectedBlockIds.length * 100} Virtual Nodes. Handshakes registered on Secure Payment Provider routing.
                    </span>
                  </div>
                )}

                {bulkStatus === 'complete_lockdown' && (
                  <div className="p-3 border border-red-500/35 bg-red-950/15 rounded-xl flex items-center gap-2.5">
                    <Lock className="w-4 h-4 text-red-400 shrink-0" />
                    <span className="text-[9.5px] font-mono text-red-400 uppercase font-black tracking-wider leading-snug">
                      NexoraX Lockdown applied. Node limits secured. Access blocked for {selectedBlockIds.length} active enterprise domains.
                    </span>
                  </div>
                )}

                {selectedBlockIds.length === 0 && (
                  <div className="p-4 rounded-xl border border-dashed border-neutral-800 text-center text-slate-500 font-mono text-[10px] uppercase">
                    No blocks currently selected. Highlight blocks on the atlas to unleash mass actions.
                  </div>
                )}
              </div>

              {/* Cumulative Financial Allocations block */}
              {selectedBlockIds.length > 0 && (
                <div className="border-t border-neutral-900 pt-4 mt-2 space-y-4">
                  <div className="p-3 bg-neutral-950 border border-neutral-900 rounded-xl space-y-2 font-mono text-[9px] uppercase tracking-wide">
                    <div className="flex justify-between font-bold text-slate-400">
                      <span>Cumulative Budget ({selectedBlockIds.length} Blocks)</span>
                      <span>£{bulkTotalDeposit.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-bold text-amber-500">
                      <span>NexoraX Levi (1%)</span>
                      <span>£{bulkLevyVal.toFixed(2)} (<span className="text-[8px]">£{bulkSplitVal.toFixed(2)}x3</span>)</span>
                    </div>
                    <div className="flex justify-between font-bold text-amber-500">
                      <span>10% Maintenance & Hosting Fee (20%)</span>
                      <span>£{bulkGavelFeeVal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-black text-emerald-400 border-t border-neutral-900 pt-1.5 mt-1">
                      <span>SecurePayment Settlement</span>
                      <span>£{bulkNetSettleVal.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={triggerBulkDeploy}
                      disabled={isLocked || bulkStatus.startsWith('running_')}
                      className="py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-black font-mono text-[9px] font-black uppercase tracking-widest rounded-lg flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <Zap className="w-3 h-3 fill-current" />
                      <span>Bulk Deploy</span>
                    </button>
                    <button
                      onClick={triggerBulkLockdown}
                      disabled={isLocked || bulkStatus.startsWith('running_')}
                      className="py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white font-mono text-[9px] font-black uppercase tracking-widest rounded-lg flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <Lock className="w-3 h-3 text-white" />
                      <span>Bulk Lock</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Single Select Mode Visualizing Detailed Block */
            activeBlock ? (
              <div className="space-y-5 flex-1 flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="flex justify-between items-center text-[9px] font-mono text-slate-500 border-b border-neutral-900 pb-3 mb-1 uppercase tracking-wider font-bold">
                    <span>Block Detail Interface</span>
                    <span className="text-[#f59e0b] font-black">BLOCK-{activeBlock.id}</span>
                  </div>

                  <div>
                    <span className="text-[8px] font-mono text-slate-500 uppercase tracking-widest mb-1 block">Category classification:</span>
                    <div className="text-xs font-bold text-[#f59e0b] uppercase font-mono tracking-wider">{activeBlock.category}</div>
                    <h3 className="text-sm font-black text-white uppercase tracking-wider font-sans mt-1">
                      {activeBlock.name}
                    </h3>
                    <p className="text-slate-400 font-sans text-xs mt-2.5 leading-relaxed">
                      {activeBlock.description}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <span className="text-[8px] font-mono font-bold text-slate-500 uppercase tracking-widest block">Virtualized Node Sub-Matrix:</span>
                    <div className="grid grid-cols-5 gap-1.5 font-mono text-[8px] text-center text-slate-400">
                      {Array.from({ length: 10 }, (_, subIdx) => {
                        const num = subIdx * 10 + 1;
                        return (
                          <div key={subIdx} className="p-1 border border-neutral-900 bg-neutral-950 rounded uppercase font-bold text-slate-500">
                            N{num}-{num+9}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {simulationStatus === 'running' && (
                    <div className="space-y-2 font-mono text-xs">
                      <div className="flex justify-between text-amber-500 font-extrabold">
                        <span>SYNCING 100 SUB-NODES...</span>
                        <span>{percentage}%</span>
                      </div>
                      <div className="w-full bg-[#0a0a0f] h-1.5 rounded-full overflow-hidden border border-neutral-900">
                        <div className="bg-amber-500 h-full transition-all duration-100" style={{ width: `${percentage}%` }} />
                      </div>
                    </div>
                  )}

                  {simulationStatus === 'complete' && (
                    <div className="p-3 border border-emerald-500/20 bg-emerald-950/10 rounded-xl flex items-center gap-2.5">
                      <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span className="text-[9.5px] font-mono text-emerald-400 uppercase font-black tracking-wider leading-snug">
                        100 Sub-Nodes verified. Handshake registered to Secure Payment Provider pipeline.
                      </span>
                    </div>
                  )}
                </div>

                {/* Precise Financial Allocations block */}
                <div className="border-t border-neutral-900 pt-5 mt-4 space-y-4">
                  <div className="p-3 bg-neutral-950 border border-neutral-900 rounded-xl space-y-2 font-mono text-[9px] uppercase tracking-wide">
                    <div className="flex justify-between font-bold text-slate-400">
                      <span>Audit Budget</span>
                      <span>£{depositVal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-bold text-amber-500">
                      <span>1% Levy Split</span>
                      <span>£{levyVal.toFixed(2)} (<span className="text-[8px]">£{splitVal.toFixed(2)}x3</span>)</span>
                    </div>
                    <div className="flex justify-between font-bold text-amber-500">
                      <span>10% Maintenance & Hosting Fee</span>
                      <span>£{gavelFeeVal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-black text-emerald-400 border-t border-neutral-900 pt-1.5 mt-1">
                      <span>SecurePayment Settlement</span>
                      <span>£{netSettleVal.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={runSimulation}
                      disabled={isLocked || simulationStatus === 'running'}
                      className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-black font-mono text-[9px] font-black uppercase tracking-widest rounded-lg flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <Play className="w-3 h-3 fill-current" />
                      <span>Active Sync</span>
                    </button>
                    <a
                      href="https://buy.stripe.com/8x2bIU2t59oYgTtfxQaMU00"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="py-2.5 px-3.5 border border-neutral-800 hover:border-amber-500 bg-[#060609] text-amber-400 font-mono text-[9px] font-bold uppercase tracking-widest rounded-lg text-center"
                    >
                      Deploy Slot
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col justify-center items-center p-6 text-center text-slate-500">
                <Cpu className="w-8 h-8 text-neutral-800 mb-2" />
                <div className="text-[10px] font-mono uppercase tracking-widest font-black text-neutral-600">No Block Selected</div>
                <p className="text-[9px] font-mono uppercase tracking-wider text-neutral-700 max-w-[200px] mt-1.5 leading-relaxed">
                  Click any of the 100 enterprise blocks on the left grid atlas to investigate its 100 integrated sub-nodes, or toggle Multi-Select Mode above for bulk lockdowns.
                </p>
              </div>
            )
          )}
        </div>
      </div>
      ) : (
        <NexoraXD3Visualizer />
      )}
    </div>
  );
};

